import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UnitList.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.milstudio.in/';

const MenuList = () => {
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [deleteModal, setDeleteModal] = useState({ show: false, menuId: null, menuName: '' });
  const [editModal, setEditModal] = useState({ show: false, menu: null });
  const [categories, setCategories] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [editForm, setEditForm] = useState({
    menuName: '',
    price: '',
    categoryId: '',
    roomTypeId: '',
    photo: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchMenus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/list-menu.php`);
      const data = await response.json();
      console.log('Menu API response:', data); // Debug log
      if (data.status && Array.isArray(data.menu)) {
        const mappedMenus = data.menu.map(item => ({
          id: Number(item.id),
          name: item.menuname,
          price: Number(item.price),
          photo: item.image_url ? `${item.image_url}` : '',
          category: item.categoryname || 'N/A',
          categoryId: Number(item.category_id) || 0,
          roomType: item.room_type || item.roomtype || 'N/A',
          roomTypeId: Number(item.room_type_id) || Number(item.roomtype_id) || 0,
          createdAt: item.create_at
                    }));
        console.log('Mapped menus:', mappedMenus); // Debug log
        setMenus(mappedMenus);
      } else {
        setMenus([]);
      }
    } catch (err) {
      setError('Failed to fetch menu data.');
      setMenus([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, [refreshTrigger]);

  useEffect(() => {
    // Fetch categories and room types for edit form
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/list-categories.php`);
        const data = await response.json();
        console.log('Categories response:', data); // Debug log
        if (data.status && Array.isArray(data.categories)) {
          setCategories(data.categories);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    const fetchRoomTypes = async () => {
      try {
        const response = await fetch(`${API_URL}/list-room-types.php`);
        const data = await response.json();
        console.log('Room types response:', data); // Debug log
        if (data.status && Array.isArray(data.room_types)) {
          setRoomTypes(data.room_types);
        }
      } catch (err) {
        console.error('Error fetching room types:', err);
      }
    };

    fetchCategories();
    fetchRoomTypes();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const filteredMenus = menus.filter(menu => {
    const matchesSearch =
      menu.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (menu.category && menu.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (menu.roomType && menu.roomType.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const handleEdit = (menu) => {
    let categoryId = '';
    if (menu.categoryId && menu.categoryId !== 0 && menu.categoryId !== '0') {
      categoryId = menu.categoryId.toString();
    } else if (menu.category && menu.category !== 'N/A' && categories.length > 0) {
      const matchingCategory = categories.find(cat => 
        cat.categoryname && cat.categoryname.toLowerCase() === menu.category.toLowerCase()
      );
      if (matchingCategory) {
        categoryId = matchingCategory.id.toString();
      }
    }

    let roomTypeId = '';
    if (menu.roomTypeId && menu.roomTypeId !== 0 && menu.roomTypeId !== '0') {
      roomTypeId = menu.roomTypeId.toString();
    } else if (menu.roomType && menu.roomType !== 'N/A' && roomTypes.length > 0) {
      const matchingRoomType = roomTypes.find(rt => 
        rt.room_type && rt.room_type.toLowerCase() === menu.roomType.toLowerCase()
      );
      if (matchingRoomType) {
        roomTypeId = matchingRoomType.id.toString();
      }
    }

    setEditForm({
      menuName: menu.name,
      price: menu.price.toString(),
      categoryId: categoryId,
      roomTypeId: roomTypeId,
      photo: null
    });
    setEditModal({ show: true, menu });
  };

  const handleDeleteClick = (menuId) => {
    const menuToDelete = menus.find(menu => menu.id === menuId);
    setDeleteModal({ show: true, menuId, menuName: menuToDelete.name });
  };

  const handleDeleteConfirm = async () => {
    const { menuId, menuName } = deleteModal;
    try {
      const response = await fetch(`${API_URL}/delete-menu.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: menuId })
      });

      const data = await response.json();
      
      if (data.status) {
        // Remove from local state
        setMenus(menus.filter(menu => menu.id !== menuId));
        showToast(`Menu "${menuName}" deleted successfully`, 'success');
      } else {
        showToast(data.message || 'Failed to delete menu item', 'error');
      }
    } catch (error) {
      console.error('Error deleting menu:', error);
      showToast('Failed to delete menu item. Please try again.', 'error');
    } finally {
      setDeleteModal({ show: false, menuId: null, menuName: '' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, menuId: null, menuName: '' });
  };

  const handleEditPhotoChange = (e) => {
    setEditForm(prev => ({ ...prev, photo: e.target.files[0] }));
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const { menu } = editModal;
    
    if (!editForm.menuName.trim()) {
      showToast('Please enter a menu name', 'error');
      return;
    }
    if (!editForm.price || isNaN(editForm.price) || Number(editForm.price) <= 0) {
      showToast('Please enter a valid price', 'error');
      return;
    }
    if (!editForm.categoryId) {
      showToast('Please select a category', 'error');
      return;
    }
    if (!editForm.roomTypeId) {
      showToast('Please select a room type', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('menu_id', menu.id); // <-- Corrected field name
      formData.append('category_id', editForm.categoryId);
      formData.append('room_type_id', editForm.roomTypeId);
      formData.append('menuname', editForm.menuName);
      formData.append('price', editForm.price);
      
      // Only append image if a new photo is selected
      if (editForm.photo) {
        formData.append('image', editForm.photo);
      }

      console.log('Sending form data:', {
        menu_id: menu.id,
        category_id: editForm.categoryId,
        room_type_id: editForm.roomTypeId,
        menuname: editForm.menuName,
        price: editForm.price,
        image: !!editForm.photo
      });

      const response = await fetch(`${API_URL}/edit-menu.php`, {
        method: 'POST',
        body: formData,
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        showToast('Invalid response from server', 'error');
        return;
      }
      
      console.log('API Response:', data);
      
      if (data.status) {
        // Update the menu in the list
        setMenus(prevMenus => 
          prevMenus.map(m => 
            m.id === menu.id 
              ? {
                  ...m,
                  name: editForm.menuName,
                  price: Number(editForm.price),
                  category: categories.find(cat => cat.id === editForm.categoryId)?.categoryname || m.category,
                  categoryId: editForm.categoryId,
                  roomType: roomTypes.find(rt => rt.id === editForm.roomTypeId)?.room_type || m.roomType,
                  roomTypeId: editForm.roomTypeId,
                  photo: data.image_url ? data.image_url : (editForm.photo ? `${API_URL}uploads/menu/${editForm.photo.name}` : m.photo)
                }
              : m
          )
        );
        
        showToast(`Menu "${editForm.menuName}" updated successfully!`, 'success');
        setEditModal({ show: false, menu: null });
        setEditForm({ menuName: '', price: '', categoryId: '', roomTypeId: '', photo: null });
        setRefreshTrigger(prev => prev + 1);
      } else {
        showToast(data.message || 'Failed to update menu item', 'error');
      }
    } catch (error) {
      console.error('Error updating menu:', error);
      showToast('Failed to update menu item. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setEditModal({ show: false, menu: null });
    setEditForm({ menuName: '', price: '', categoryId: '', roomTypeId: '', photo: null });
  };

  const handleAddMenu = () => {
    navigate('/add-menu');
    showToast('Navigating to add new menu item', 'info');
  };

  return (
    <div className="menu-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Menu Management</h1>
          <p>Manage restaurant menu items</p>
        </div>
        <button className="add-unit-button" onClick={handleAddMenu}>
          + Add New Menu
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search menu by name, category, or room type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', margin: '2rem' }}>Loading menu items...</div>
      ) : error ? (
        <div style={{ color: 'red', textAlign: 'center', margin: '2rem' }}>{error}</div>
      ) : (
        <div className="unit-list-table-container">
          <table className="unit-list-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Menu Name</th>
                <th>Price</th>
                <th>Category</th>
                <th>Room Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMenus.map(menu => (
                <tr key={menu.id}>
                  <td>
                    <img src={`${process.env.REACT_APP_API_URL}${menu.photo}`} alt={menu.name} style={{ width: 40, height: 40, borderRadius: '6px', objectFit: 'cover', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }} />
                  </td>
                  <td>{menu.name}</td>
                  <td>₹{menu.price.toFixed(2)}</td>
                  <td>{menu.category}</td>
                  <td>{menu.roomType || 'N/A'}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn edit"
                        onClick={() => handleEdit(menu)}
                      >
                        Edit
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDeleteClick(menu.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="table-footer">
        <div className="results-info">
          Showing {filteredMenus.length} of {menus.length} menu items
        </div>
      </div>

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the menu item "<strong>{deleteModal.menuName}</strong>"?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Menu Modal */}
      {editModal.show && (
        <div className="modal-overlay">
          <div className="modal-content edit-menu-modal">
            <div className="modal-header">
              <h3>Edit Menu Item</h3>
            </div>
            <form onSubmit={handleEditSubmit} className="edit-menu-form">
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="editMenuName">Menu Name</label>
                  <input
                    type="text"
                    id="editMenuName"
                    value={editForm.menuName}
                    onChange={(e) => handleEditFormChange('menuName', e.target.value)}
                    placeholder="Enter menu name"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editPrice">Price</label>
                  <input
                    type="number"
                    id="editPrice"
                    value={editForm.price}
                    onChange={(e) => handleEditFormChange('price', e.target.value)}
                    placeholder="Enter price"
                    min="0.01"
                    step="0.01"
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editPhoto">Photo</label>
                  {editModal.menu && editModal.menu.photo && (
                    <div className="current-photo">
                      <img 
                        src={`${process.env.REACT_APP_API_URL}${editModal.menu.photo}`} 
                        alt="Current photo" 
                        style={{ 
                          width: 80, 
                          height: 80, 
                          borderRadius: '8px', 
                          objectFit: 'cover', 
                          marginBottom: '10px',
                          border: '2px solid #e5e7eb'
                        }} 
                      />
                      <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 10px 0' }}>
                        Current photo
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    id="editPhoto"
                    accept="image/*"
                    onChange={handleEditPhotoChange}
                    disabled={isSubmitting}
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '5px 0 0 0' }}>
                    Leave empty to keep current photo
                  </p>
                </div>
                <div className="form-group">
                  <label htmlFor="editCategory">Category</label>
                  <select
                    id="editCategory"
                    value={editForm.categoryId}
                    onChange={(e) => handleEditFormChange('categoryId', e.target.value)}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.categoryname}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="editRoomType">Room Type</label>
                  <select
                    id="editRoomType"
                    value={editForm.roomTypeId}
                    onChange={(e) => handleEditFormChange('roomTypeId', e.target.value)}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select room type</option>
                    {roomTypes.map((rt) => (
                      <option key={rt.id} value={rt.id}>{rt.type}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleEditCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Updating...' : 'Update Menu'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuList;